<script>
import { useElixioConnectStore } from '../../stores/elixioConnect'
import { extractDeepErrorMessage } from '../../composables/useApiErrors'
import EcButton from '../ui/EcButton.vue'
import ShipmentStatusBadge from './ShipmentStatusBadge.vue'
import EcAlert from '../ui/EcAlert.vue'
import EcCollapse from '../ui/EcCollapse.vue'
import EcSpinner from '../ui/EcSpinner.vue'
import ShipmentForm from './ShipmentForm.vue'
import PickupForm from './PickupForm.vue'
import { LABEL_TYPES } from '../../types/shipment'

export default {
  name: 'ShipmentItem',
  components: { EcButton, ShipmentStatusBadge, EcAlert, EcCollapse, EcSpinner, ShipmentForm, PickupForm },
  props: {
    shipment: { type: Object, required: true },
  },
  data() {
    return {
      store: useElixioConnectStore(),
      selectedService: null,
      serviceSearch: '',
      showDropdown: false,
      confirming: false,
      editMode: false,
      showPickupForm: false,
      showLabelSection: !!this.shipment.pickup_id,
      printing: false,
      labelType: 'EightFiveByEleven',
      showLabelReminder: false,
      LABEL_TYPES,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate || ((k) => k)
    },
    confirm() {
      return this.$ecConfirm || ((msg) => Promise.resolve(window.confirm(msg)))
    },
    notify() {
      return this.$ecNotify || (() => {})
    },
    isEstimated() {
      return this.shipment.status === 'ESTIMATED'
    },
    isCreated() {
      return ['CREATED', 'PICKUP_CREATED'].includes(this.shipment.status)
    },
    isCancelled() {
      return this.shipment.status === 'CANCELLED'
    },
    hasPickup() {
      return !!this.shipment.pickup_id
    },
    hasLabel() {
      return !!this.shipment.printed_label
    },
    hasRates() {
      return (this.shipment.ratelist ?? []).length > 0
    },
    filteredRates() {
      const rates = this.shipment.ratelist ?? []
      const q = this.serviceSearch.trim().toLowerCase()
      if (!q) return rates
      return rates.filter((r) =>
        (r.service_name ?? '').toLowerCase().includes(q) ||
        String(r.total?.value ?? '').includes(q),
      )
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside)
  },
  methods: {
    formatTime(timeObj) {
      if (!timeObj) return '-'
      return `${String(timeObj.hour).padStart(2, '0')}:${String(timeObj.minute).padStart(2, '0')}`
    },
    formatPickupDate(d) {
      if (!d) return '-'
      return `${String(d.day).padStart(2, '0')}/${String(d.month).padStart(2, '0')}/${d.year}`
    },
    selectRate(rate) {
      this.selectedService = rate.service_id
      this.serviceSearch = `${rate.service_name} — ${rate.total.value} ${rate.total.currency}`
      this.showDropdown = false
    },
    clearSelection() {
      this.selectedService = null
      this.serviceSearch = ''
      this.showDropdown = true
    },
    onClickOutside(e) {
      if (this.$refs.ratePickerRef && !this.$refs.ratePickerRef.contains(e.target)) {
        this.showDropdown = false
      }
    },
    async confirmShipment() {
      if (!this.selectedService) return
      this.confirming = true
      try {
        await this.store.createShipment(this.shipment.id, this.selectedService)
        this.notify(this.t('ELIXIO_CONNECT.SHIPMENT_CONFIRMED'), 'success')
        this.$emit('updated')
      } catch (err) {
        this.notify(extractDeepErrorMessage(err, this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')), 'error')
      } finally {
        this.confirming = false
      }
    },
    async destroyEstimation() {
      const ok = await this.confirm(this.t('ELIXIO_CONNECT.CONFIRM_DELETE_ESTIMATION'))
      if (!ok) return
      await this.store.destroyEstimation(this.shipment.id)
      this.notify(this.t('ELIXIO_CONNECT.ESTIMATION_DELETED'), 'success')
      this.$emit('updated')
    },
    async deleteShipment() {
      const ok = await this.confirm(this.t('ELIXIO_CONNECT.CONFIRM_DELETE_SHIPMENT'))
      if (!ok) return
      await this.store.destroyEstimation(this.shipment.id)
      this.notify(this.t('ELIXIO_CONNECT.SHIPMENT_DELETED'), 'success')
      this.$emit('updated')
    },
    async cancelShipment() {
      const ok = await this.confirm(this.t('ELIXIO_CONNECT.CONFIRM_CANCEL_SHIPMENT'))
      if (!ok) return
      await this.store.cancelShipment(this.shipment.id)
      this.notify(this.t('ELIXIO_CONNECT.SHIPMENT_CANCELLED'), 'success')
      this.$emit('updated')
    },
    async cancelPickup() {
      const ok = await this.confirm(this.t('ELIXIO_CONNECT.CONFIRM_CANCEL_PICKUP'))
      if (!ok) return
      await this.store.cancelPickup(this.shipment.id)
      this.notify(this.t('ELIXIO_CONNECT.PICKUP_CANCELLED'), 'success')
      this.$emit('updated')
    },
    async printLabel() {
      this.printing = true
      try {
        await this.store.printShipmentLabel(this.shipment.id, this.labelType)
        this.$emit('updated')
      } catch (err) {
        this.notify(extractDeepErrorMessage(err, this.t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')), 'error')
      } finally {
        this.printing = false
      }
    },
    openLabelLink(url) {
      window.open(url, '_blank')
    },
    handleCreatePickup() {
      if (!this.hasLabel) {
        this.showLabelReminder = true
      } else {
        this.showPickupForm = true
      }
    },
    skipLabelAndCreatePickup() {
      this.showLabelReminder = false
      this.showPickupForm = true
    },
    printLabelFirst() {
      this.showLabelReminder = false
      this.showLabelSection = true
    },
    onFormSaved() {
      this.editMode = false
      this.$emit('updated')
    },
    onPickupSubmitted() {
      this.showPickupForm = false
      this.$emit('updated')
    },
  },
}
</script>

<template>
  <div class="ec-shipment-item">
    <!-- Summary row -->
    <div class="ec-shipment-item__summary">
      <div class="ec-shipment-item__info">
        <div class="ec-flex ec-items-center ec-gap-2">
          <i class="bi bi-truck" />
          <span class="ec-fw-medium">
            {{ shipment.sender?.contact_name ?? shipment.sender?.company_name ?? '-' }}
          </span>
          <i class="bi bi-arrow-right" style="opacity: 0.4" />
          <span class="ec-fw-medium">
            {{ shipment.recipient?.attention_to ?? shipment.recipient?.company_name ?? '-' }}
          </span>
        </div>

        <div v-if="isCreated && shipment.selected_rate" class="ec-text-muted" style="font-size: 0.8125rem">
          {{ shipment.selected_rate.service_name }} —
          {{ shipment.selected_rate.total.value }} {{ shipment.selected_rate.total.currency }}
        </div>
      </div>

      <ShipmentStatusBadge :status="shipment.status" />
    </div>

    <!-- Estimated: select service & confirm -->
    <div v-if="isEstimated && !editMode" class="ec-shipment-item__section">
      <!-- No rates available -->
      <template v-if="!hasRates">
        <div>
          <EcAlert type="warning">
            {{ t('ELIXIO_CONNECT.NO_RATES_AVAILABLE') }}
          </EcAlert>
          <div class="ec-flex ec-gap-2 ec-mt-2">
            <EcButton variant="outline-primary" size="sm" icon="pencil" @click="editMode = true">
              {{ t('ELIXIO_CONNECT.EDIT_AND_REESTIMATE') }}
            </EcButton>
            <EcButton variant="outline-danger" size="sm" icon="trash" @click="destroyEstimation">
              {{ t('ELIXIO_CONNECT.DELETE') }}
            </EcButton>
          </div>
        </div>
      </template>

      <!-- Rates available -->
      <template v-else>
        <div>
          <label class="ec-form-label">{{ t('ELIXIO_CONNECT.AVAILABLE_RATES') }}</label>
          <div ref="ratePickerRef" class="ec-rate-picker ec-mb-2">
            <div class="ec-rate-picker__input-wrap">
              <input
                v-model="serviceSearch"
                type="text"
                class="ec-input__field"
                :placeholder="t('ELIXIO_CONNECT.SELECT_SERVICE')"
                @focus="function() { if (!selectedService) showDropdown = true }"
                @input="function() { if (!selectedService) showDropdown = true }"
              />
              <button v-if="selectedService" type="button" class="ec-rate-picker__clear" @click="clearSelection">
                <i class="bi bi-x-lg" />
              </button>
            </div>
            <div v-if="showDropdown && !selectedService && filteredRates.length" class="ec-rate-picker__dropdown">
              <div
                v-for="rate in filteredRates"
                :key="rate.service_id"
                class="ec-rate-picker__option"
                @click="selectRate(rate)"
              >
                {{ rate.service_name }} — {{ rate.total.value }} {{ rate.total.currency }}
              </div>
            </div>
            <div v-if="showDropdown && !selectedService && serviceSearch && !filteredRates.length" class="ec-rate-picker__dropdown ec-rate-picker__empty">
              {{ t('ELIXIO_CONNECT.NO_RESULTS') }}
            </div>
          </div>

          <div class="ec-flex ec-gap-2">
            <EcButton variant="success" size="sm" icon="check-lg" :disabled="!selectedService || confirming" @click="confirmShipment">
              <EcSpinner v-if="confirming" size="sm" style="margin-right: 0.25rem" />
              {{ t('ELIXIO_CONNECT.CONFIRM_SHIPMENT') }}
            </EcButton>
            <EcButton variant="outline-secondary" size="sm" icon="pencil" @click="editMode = true">
              {{ t('ELIXIO_CONNECT.EDIT') }}
            </EcButton>
            <EcButton variant="outline-danger" size="sm" icon="trash" @click="destroyEstimation">
              {{ t('ELIXIO_CONNECT.DELETE') }}
            </EcButton>
          </div>
        </div>
      </template>
    </div>

    <!-- Edit mode -->
    <div v-if="editMode" class="ec-shipment-item__section">
      <ShipmentForm
        :shippable-type="shipment.shippable_type"
        :shippable-id="shipment.shippable_id"
        :initial-data="shipment"
        @saved="onFormSaved"
        @cancel="editMode = false"
      />
    </div>

    <!-- Created: actions -->
    <div v-if="isCreated && !showPickupForm && !showLabelSection && !showLabelReminder" class="ec-shipment-item__section">
      <div class="ec-flex ec-gap-2">
        <EcButton v-if="!hasPickup" variant="primary" size="sm" icon="box-arrow-up" @click="handleCreatePickup">
          {{ t('ELIXIO_CONNECT.CREATE_PICKUP') }}
        </EcButton>
        <EcButton variant="outline-primary" size="sm" icon="printer" @click="showLabelSection = true">
          {{ t('ELIXIO_CONNECT.LABEL') }}
        </EcButton>
        <EcButton variant="outline-danger" size="sm" icon="x-circle" @click="cancelShipment">
          {{ t('ELIXIO_CONNECT.CANCEL') }}
        </EcButton>
      </div>
    </div>

    <!-- Label reminder before pickup -->
    <div v-if="showLabelReminder" class="ec-shipment-item__section">
      <EcAlert type="warning">
        {{ t('ELIXIO_CONNECT.LABEL_REMINDER_BEFORE_PICKUP') }}
      </EcAlert>
      <div class="ec-flex ec-gap-2 ec-mt-2">
        <EcButton variant="primary" size="sm" icon="printer" @click="printLabelFirst">
          {{ t('ELIXIO_CONNECT.PRINT_LABEL_FIRST') }}
        </EcButton>
        <EcButton variant="outline-secondary" size="sm" icon="arrow-right" @click="skipLabelAndCreatePickup">
          {{ t('ELIXIO_CONNECT.CONTINUE_WITHOUT_LABEL') }}
        </EcButton>
      </div>
    </div>

    <!-- Cancelled: allow deletion -->
    <div v-if="isCancelled" class="ec-shipment-item__section">
      <div class="ec-flex ec-gap-2">
        <EcButton variant="outline-danger" size="sm" icon="trash" @click="deleteShipment">
          {{ t('ELIXIO_CONNECT.DELETE') }}
        </EcButton>
      </div>
    </div>

    <!-- Pickup details (if pickup exists) -->
    <EcCollapse v-if="hasPickup" :model-value="true" :title="t('ELIXIO_CONNECT.PICKUP_DETAILS')">
      <div class="ec-shipment-item__pickup">
        <div class="ec-shipment-item__pickup-grid">
          <div>
            <span class="ec-text-muted" style="font-size: 0.75rem">{{ t('ELIXIO_CONNECT.PICKUP_DATE') }}</span>
            <p style="margin: 0">{{ formatPickupDate(shipment.pickup_data?.date) }}</p>
          </div>
          <div>
            <span class="ec-text-muted" style="font-size: 0.75rem">{{ t('ELIXIO_CONNECT.READY_AT') }}</span>
            <p style="margin: 0">
              {{ formatTime(shipment.pickup_data?.ready_at) }} → {{ formatTime(shipment.pickup_data?.ready_until) }}
            </p>
          </div>
          <div>
            <span class="ec-text-muted" style="font-size: 0.75rem">{{ t('ELIXIO_CONNECT.PICKUP_LOCATION') }}</span>
            <p style="margin: 0">{{ shipment.pickup_data?.pickup_location }}</p>
          </div>
          <div>
            <span class="ec-text-muted" style="font-size: 0.75rem">{{ t('ELIXIO_CONNECT.TOTAL_WEIGHT') }}</span>
            <p style="margin: 0">
              {{ shipment.pickup_data?.package_details?.weight?.value }}
              {{ shipment.pickup_data?.package_details?.weight?.unit }}
              ({{ shipment.pickup_data?.package_details?.quantity }} {{ t('ELIXIO_CONNECT.PACKAGES') }})
            </p>
          </div>
        </div>
        <p v-if="shipment.pickup_data?.pickup_instructions" class="ec-text-muted" style="font-size: 0.8125rem; margin: 0.5rem 0 0">
          <i class="bi bi-chat-left-text" /> {{ shipment.pickup_data.pickup_instructions }}
        </p>
        <EcButton variant="outline-danger" size="sm" icon="x-circle" class="ec-mt-2" @click="cancelPickup">
          {{ t('ELIXIO_CONNECT.CANCEL_PICKUP') }}
        </EcButton>
      </div>
    </EcCollapse>

    <!-- Pickup form -->
    <div v-if="showPickupForm" class="ec-shipment-item__section">
      <PickupForm
        :shipment="shipment"
        @submitted="onPickupSubmitted"
        @cancel="showPickupForm = false"
      />
    </div>

    <!-- Label section -->
    <div v-if="showLabelSection && isCreated" class="ec-shipment-item__section">
      <div class="ec-shipment-item__label-bar">
        <select v-model="labelType" class="ec-select__field" style="max-width: 200px">
          <option v-for="lt in LABEL_TYPES" :key="lt" :value="lt">
            {{ t('ELIXIO_CONNECT.' + lt) }}
          </option>
        </select>
        <EcButton variant="primary" size="sm" icon="printer" :disabled="printing || hasPickup" @click="printLabel">
          <EcSpinner v-if="printing" size="sm" style="margin-right: 0.25rem" />
          {{ t('ELIXIO_CONNECT.PRINT_LABEL') }}
        </EcButton>
        <EcButton variant="outline-secondary" size="sm" icon="x-lg" :disabled="printing" @click="showLabelSection = false" />
      </div>

      <!-- Printed label link -->
      <div
        v-if="shipment.printed_label"
        class="ec-shipment-item__label-card"
        @click="openLabelLink(shipment.printed_label.shipping_label)"
      >
        <i class="bi bi-file-earmark-text" style="font-size: 1.5rem; color: var(--ec-primary)" />
        <div>
          <span class="ec-fw-medium">
            {{ shipment.printed_label.type ? t('ELIXIO_CONNECT.' + shipment.printed_label.type) : t('ELIXIO_CONNECT.LABEL') }}
          </span>
          <span class="ec-text-muted" style="font-size: 0.8125rem; display: block">
            {{ shipment.printed_label.shipping_label?.slice(0, 50) }}{{ (shipment.printed_label.shipping_label?.length ?? 0) > 50 ? '...' : '' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ec-shipment-item {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ec-shipment-item__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.ec-shipment-item__info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.ec-shipment-item__section {
  padding: 0.75rem;
  background: var(--ec-bg-muted, #f8f9fa);
  border-radius: 8px;
  border: 1px solid var(--ec-border-color, #e2e8f0);
  overflow: visible;
}

.ec-shipment-item__pickup {
  padding: 0.75rem;
}

.ec-shipment-item__pickup-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.ec-shipment-item__label-bar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ec-shipment-item__label-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  margin-top: 0.5rem;
  border: 1px solid var(--ec-primary, #3b82f6);
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}

.ec-shipment-item__label-card:hover {
  background: rgba(59, 130, 246, 0.04);
}

/* Rate picker (filterable select) */
.ec-rate-picker {
  position: relative;
}

.ec-rate-picker__input-wrap {
  position: relative;
}

.ec-rate-picker__input-wrap input {
  padding-right: 2rem;
}

.ec-rate-picker__clear {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ec-text-muted, #6b7280);
  padding: 0.125rem;
  font-size: 0.75rem;
  line-height: 1;
}

.ec-rate-picker__clear:hover {
  color: var(--ec-text-color, #1f2937);
}

.ec-rate-picker__dropdown {
  position: absolute;
  z-index: 1050;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 220px;
  overflow-y: auto;
  background: var(--ec-bg, #fff);
  border: 1px solid var(--ec-border-color, #e2e8f0);
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.ec-rate-picker__option {
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background 0.1s;
}

.ec-rate-picker__option:hover {
  background: var(--ec-bg-muted, #f3f4f6);
}

.ec-rate-picker__empty {
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: var(--ec-text-muted, #6b7280);
  text-align: center;
}
</style>
